import hasPermission, { ActivePermission } from '../../Hooks/hasPermission';

/**
 * QUIÉN PUEDE ESCRIBIR EN LOS CATÁLOGOS DE TERRESTRE (transportistas, fronteras, modos, oficinas y tipos de entrega).
 *
 * ## 🔴 Lo que resuelve (decisión 15, 12-oct-2026)
 *
 * Los formularios de cargas ofrecían *Add carrier*, *Add border* y *Add mode* a todo el mundo, y el de bodegas *Add
 * commodity*, pero el backend pedía `CATALOGS`: 9 de los 15 usuarios activos que escriben cargas no lo tenían, así que
 * para ellos el botón no hacía nada y no decía por qué. El usuario: *"si no tienen permiso no te deje agregarlos en ambas
 * partes: front no se muestre y back no te deje"*.
 *
 * ## 🔑 Una sola regla para la pantalla y para el backend
 *
 * Escribir pide **el permiso del módulo (`CATALOGS`) y el de la acción (`CT_CREATE`, `CT_EDIT` o `CT_DELETE`)**, igual
 * que `catalogRoutes.ts`. Antes la pantalla de Catálogos miraba solo los `CT_*` y el backend solo `CATALOGS`: dos reglas
 * que coincidían por casualidad (los 6 usuarios activos con uno tienen todos, medido el 12-oct). Con una regla en cada
 * lado, el primer usuario con uno y no el otro habría visto botones que responden 403.
 *
 * 📌 No aplica a puertos y aeropuertos: ese catálogo trae su propio `writePermission` (`DEV_ACCESS`).
 */
export type CatalogAction = 'create' | 'edit' | 'delete';

const ACTION_PERMISSION: Record<CatalogAction, string> = {
  create: 'CT_CREATE',
  edit: 'CT_EDIT',
  delete: 'CT_DELETE',
};

/** El permiso del módulo. Sin él no se escribe en ningún catálogo de terrestre, tenga o no los `CT_*`. */
export const CATALOG_MODULE_PERMISSION = 'CATALOGS';

export const canWriteCatalog = (
  permissions: ActivePermission[] | null | undefined,
  action: CatalogAction
): boolean =>
  hasPermission(permissions, CATALOG_MODULE_PERMISSION) && hasPermission(permissions, ACTION_PERMISSION[action]);
